import {
  ArrayField,
  ArrayFieldApi,
  FormApi,
  useArrayFieldItemApi,
  useArrayFieldItemState,
} from 'informed';
import { RefObject, useRef, useState } from 'react';
import classNames from 'classnames';
import Emoji from '../Emoji';
import moment from 'moment';
import { TyInput, TyNumberInput, TyTimeInput } from '@tymbe/ui-forms';
import { getDurationString, maxDuration } from './utils';
import { Button, SecondaryButton } from '@tymbe/ui-primitives/*';
import { ActionIcon, FavouriteIcon, SocialIcon } from '@tymbe/ui-icons';
import Collapse from '../../../../components/Collapse';
import { ShiftItem } from './types';
import InvitationItem from './InvitationItem';
import Tooltip from 'rc-tooltip';
import { useFormValueMonitor } from './useFormValueMonitor';
import { ShiftTemplateData } from '@tymbe/schema/shift-template.interface';

interface ShiftItemProps {
  invitationsRef: React.RefObject<FormApi<any> | undefined>;
  onEdit?: (shift: ShiftItem, index: number) => void;
  getInvitations?: () => { id: string; selected: boolean }[];
  getShiftTemplate: (id?: string | number) => ShiftTemplateData | undefined;
}

const ShiftListItem = ({
  invitationsRef,
  onEdit,
  getInvitations = () => [],
  getShiftTemplate,
}: ShiftItemProps) => {
  const {
    values: shift,
    key,
    index,
    initialValue,
  } = useArrayFieldItemState<ShiftItem>();
  const { remove, setValue } = useArrayFieldItemApi();
  const [open, setOpen] = useState(false);
  const addInvitationRef = useRef<ArrayFieldApi['addWithInitialValue']>(null);

  const addInvitations = () => {
    const invitations = getInvitations().filter(
      ({ id }) =>
        !shift.invitations?.find((inv) => String(inv.id) === String(id))
    );

    const totalInvitations =
      (shift.invitations?.length || 0) + invitations.length;
    if (totalInvitations > Number(shift.slots)) {
      setValue('slots', totalInvitations);
    }

    for (const invitation of invitations) {
      addInvitationRef.current?.({
        ...invitation,
        selected: false,
      });
    }
  };

  const [_, setInvitationFormValues] = useState<any>(null);

  useFormValueMonitor(
    invitationsRef as RefObject<FormApi<any>>,
    'persons',
    (persons) => setInvitationFormValues(persons)
  );

  const itemsInInviteSelectorOtherThanAlreadyInvited = getInvitations().filter(
    ({ id }) =>
      !shift?.invitations?.find((inv) => String(inv.id) === String(id))
  ).length;

  const canSubmitCurrentInvitations =
    itemsInInviteSelectorOtherThanAlreadyInvited !== 0;

  return (
    <div
      key={key}
      onClick={(e) => {
        // if interactive element
        if (
          e.target instanceof HTMLElement &&
          !e.target.closest('button, input, select, textarea')
        ) {
          if (shift?.invitations?.length && shift.invitations.length > 0) {
            setOpen(!open);
          }
        }
      }}
      className={classNames(
        'grid grid-cols-subgrid col-span-full border border-b-0 last:border-b first:rounded-t-3xl last:rounded-b-3xl border-secondary-transparent-10 px-4 items-center py-1 transition-colors',
        'hover:bg-secondary-transparent-5 hover:cursor-pointer',
        { 'bg-secondary-transparent-5': open }
      )}
    >
      <div className="flex items-center gap-2 h-[72px]">
        <Emoji emoji="🐛" className="size-8 bg-secondary-50" />
        <div>
          <div className="text-secondary-600">
            {moment(shift?.date).format('L')}
          </div>
          <div className="text-secondary-500 font-normal">
            {getShiftTemplate(shift?.templateId)?.template_name}
          </div>
        </div>
      </div>
      <div className="flex gap-2.5 items-center text-secondary-500">
        <TyTimeInput
          simple
          className="w-16"
          name="startTime"
          validateWhen={['endTime']}
          validate={maxDuration}
          validateOn="change"
        />
        -
        <TyTimeInput
          simple
          className="w-16"
          name="endTime"
          validateWhen={['startTime']}
          validate={maxDuration}
          validateOn="change"
        />
        {getDurationString(shift?.startTime, shift?.endTime)}
      </div>
      <div className="text-secondary-500">
        <TyNumberInput
          name="slots"
          className="w-32"
          min={shift?.invitations?.length ?? 0}
          validate={(value) =>
            Number(value) < Number(shift?.invitations?.length || 0)
              ? 'Snažíte se pozvat více brigádníků než je volných míst.'
              : undefined
          }
        />
      </div>
      <div>{shift?.invitations?.length ?? 0}</div>
      <div className="flex items-center gap-2">
        <Tooltip
          placement="top"
          trigger={['hover']}
          overlay="Nejprve si v levém panelu vytvořte seznam tymberů"
          overlayClassName={classNames('max-w-[200px]', {
            '!hidden': itemsInInviteSelectorOtherThanAlreadyInvited !== 0,
          })}
        >
          <div className="flex-grow">
            <SecondaryButton
              variant="outlined"
              className={classNames(
                !canSubmitCurrentInvitations ||
                  itemsInInviteSelectorOtherThanAlreadyInvited === 0
                  ? null
                  : 'text-white !bg-secondary-600 p-2',
                'gap-2 w-full h-[42px]'
              )}
              onClick={() => addInvitations()}
              disabled={
                !canSubmitCurrentInvitations ||
                itemsInInviteSelectorOtherThanAlreadyInvited === 0
              }
              type="button"
            >
              <SocialIcon name="person_add" className="size-4" />
              <span className="text-body-2-md font-semibold">
                Pozvat ({itemsInInviteSelectorOtherThanAlreadyInvited})
              </span>
            </SecondaryButton>
          </div>
        </Tooltip>
        <Button
          className="border border-secondary-300 p-2.5"
          onClick={() => onEdit?.({ ...initialValue, ...shift }, index)}
          type="button"
        >
          <FavouriteIcon name="mode_edit" className="size-5" />
        </Button>
        <Button
          className="border border-red-300 text-red-500 p-2.5"
          onClick={() => remove()}
          type="button"
        >
          <ActionIcon name="delete_outline" className="size-5" />
        </Button>
      </div>
      <Collapse
        open={open}
        className="col-start-3 col-span-3 flex flex-col *:divide-y *:divide-secondary-100"
      >
        <ArrayField name="invitations">
          {({ addWithInitialValue }) => {
            addInvitationRef.current = addWithInitialValue;
            return (
              <ArrayField.Items>
                {(props) => {
                  return <InvitationItem />;
                }}
              </ArrayField.Items>
            );
          }}
        </ArrayField>
      </Collapse>
      {/* to keep in informed values */}
      <TyInput name="templateId" className="hidden" />
      <TyInput name="date" className="hidden" />
      <TyInput name="contact" className="hidden" />
      <TyInput name="requirements" className="hidden" />
      <TyInput name="utilities" className="hidden" />
      <TyInput name="noPreferences" className="hidden" />
      <TyInput name="menOnly" className="hidden" />
      <TyInput name="womenOnly" className="hidden" />
      <TyInput name="men" className="hidden" />
      <TyInput name="women" className="hidden" />
      <TyInput name="contestStart" className="hidden" />
      <TyInput name="contestEndVariant" className="hidden" />
      <TyInput name="contestEndTime" className="hidden" />
    </div>
  );
};

export default ShiftListItem;
